import { useId, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label, RequiredMark } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { WorkflowView } from '@/lib/api/generated/model';
import {
  AI_HELP_COPY,
  AiToolLine,
  draftWorkflowAsk,
  useAiToolHelp,
} from '@/features/editor-access';
import { useWorkspace } from '@/features/workspaces/hooks/useWorkspaces';

import { isWorkflowRejection } from '../errors';
import { useCreateWorkflowDraft } from '../hooks/useCreateWorkflowDraft';

/** The bound the service enforces; mirrored so the field can refuse early. */
const NAME_MAX_LENGTH = 200;

/**
 * Creates an empty workflow from a name, and opens it in the builder.
 *
 * A name is all that is asked for, because a name is all the service needs: the
 * key is derived from it, and the definition is what the builder is for. Asking
 * for phases or a first step here would be asking the user to design before
 * they can see anything.
 *
 * Navigation on success is the point of the dialog, not a courtesy — "create a
 * workflow" means "start editing one", and leaving the user on the list to find
 * their new row would make creating and opening two separate chores.
 */
export function CreateWorkflowDialog({
  workspaceId,
  open,
  onOpenChange,
  onCreated,
  description:
    purpose = 'Give it a name to start. You will build its phases and steps on the canvas, and publish it when it is ready.',
}: {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * What to do with the new draft instead of opening it (spec 202). The
   * sub-workflow picker links its step to the draft and keeps the person on the
   * parent they are building, unsaved edits and all.
   */
  onCreated?: (workflow: WorkflowView) => void;
  /** The dialog's explanation, when the reason for creating differs. */
  description?: string;
}) {
  const navigate = useNavigate();
  const create = useCreateWorkflowDraft(workspaceId);
  const aiHelp = useAiToolHelp();
  // The shell has already read it, so naming the workspace costs nothing.
  const workspaceName = useWorkspace(workspaceId).data?.name;
  const { error, isPending } = create;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const nameId = useId();
  const descriptionId = useId();
  const errorId = useId();

  const trimmed = name.trim();
  // Length is checked against the untrimmed value on purpose: the service
  // trims, so a 205-character name of which five are spaces is accepted, and
  // refusing it here would be stricter than the thing we are mirroring.
  const tooLong = trimmed.length > NAME_MAX_LENGTH;
  const canSubmit = trimmed.length > 0 && !tooLong && !isPending;

  const handleOpenChange = (next: boolean) => {
    if (isPending) return;
    onOpenChange(next);
    if (!next) {
      create.reset();
      setName('');
      setDescription('');
    }
  };

  const submit = () => {
    if (!canSubmit) return;
    create.mutate(
      { name: trimmed, description: description.trim() || null },
      {
        onSuccess: (workflow) => {
          onOpenChange(false);
          create.reset();
          setName('');
          setDescription('');
          if (onCreated) {
            onCreated(workflow);
            return;
          }
          void navigate({
            to: '/workspaces/$workspaceId/workflows/$workflowKey/builder',
            params: { workspaceId, workflowKey: workflow.key },
          });
        },
      },
    );
  };

  // A key collision is the one rejection a user can provoke here, and it is
  // about the name they typed — so it belongs beside the field rather than in a
  // banner about the operation.
  const violations = isWorkflowRejection(error) ? error.violations : [];
  const message =
    violations.length > 0
      ? violations.map((violation) => violation.message).join(' ')
      : error !== null
        ? error.message
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New workflow</DialogTitle>
          <DialogDescription>{purpose}</DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="flex flex-col gap-1.5">
            {/*
              Marked required, matching the builder's inspector, so one rule
              holds wherever this app asks for a name. The "(optional)" below
              stays: the two are not rival conventions once required fields are
              always marked — an unmarked label is unambiguous, and saying so
              out loud still earns its place on a field people hesitate over.
            */}
            <div className="flex items-center gap-0.5">
              <Label htmlFor={nameId}>Name</Label>
              <RequiredMark />
            </div>
            <Input
              id={nameId}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                if (error !== null) create.reset();
              }}
              // Not `required`: the browser's own bubble would say "fill in
              // this field" in a voice that is not ours, and the submit button
              // already refuses.
              aria-invalid={message !== null || tooLong}
              aria-describedby={message !== null ? errorId : undefined}
              autoFocus
              placeholder="Specification review"
            />
            {tooLong && (
              <p className="text-destructive text-xs">
                A name may be at most {NAME_MAX_LENGTH} characters.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={descriptionId}>
              Description{' '}
              <span className="text-muted-foreground font-normal">
                (optional)
              </span>
            </Label>
            <Textarea
              id={descriptionId}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={2}
              placeholder="What this workflow is for."
            />
          </div>

          {message !== null && (
            <p id={errorId} role="alert" className="text-destructive text-sm">
              {message}
            </p>
          )}

          {/*
            The last line above the buttons, never beside them: another way
            in, not a rival to Create (spec 248). Not below them either: the
            footer band runs to the dialog's edge, and a line under it sat on
            a second surface. Not where the new draft is linked as a step
            (`onCreated`): a draft written in an AI tool cannot be linked back.
          */}
          {aiHelp !== 'hidden' && onCreated === undefined ? (
            <AiToolLine
              help={aiHelp}
              ask={draftWorkflowAsk({ id: workspaceId, name: workspaceName })}
              copyWords={AI_HELP_COPY.workflowCopy}
              setupWords={AI_HELP_COPY.workflowSetUp}
            />
          ) : null}

          <DialogFooter>
            <DialogClose
              render={<Button variant="outline" disabled={isPending} />}
            >
              Cancel
            </DialogClose>
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? 'Creating…' : 'Create workflow'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
